import { useEffect, useState } from "react";
import { Sun, Moon } from "lucide-react";
import { getTheme, applyTheme } from "@/lib/theme";

// Compact day / night toggle. Pill shaped, slides a champagne knob left
// (night) or right (day). Persists to localStorage via lib/theme so no
// state lift is needed anywhere else.
export default function ThemeToggle({ compact = false, className = "" }) {
  const [theme, setTheme] = useState(() => getTheme());

  useEffect(() => {
    const onChange = (e) => setTheme(e.detail);
    window.addEventListener("cs:theme-changed", onChange);
    return () => window.removeEventListener("cs:theme-changed", onChange);
  }, []);

  const isLight = theme === "light";
  const toggle = () => applyTheme(isLight ? "dark" : "light");

  if (compact) {
    return (
      <button
        onClick={toggle}
        aria-label={isLight ? "Switch to night mode" : "Switch to day mode"}
        data-testid="theme-toggle-compact"
        className={`inline-flex items-center justify-center w-8 h-8 rounded-full transition-colors hover:bg-white/10 ${className}`}
      >
        {isLight ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" style={{ color: "#f2dfb5" }} />}
      </button>
    );
  }

  return (
    <button
      onClick={toggle}
      role="switch"
      aria-checked={isLight}
      aria-label={isLight ? "Switch to night mode" : "Switch to day mode"}
      data-testid="theme-toggle"
      className={`relative inline-flex items-center gap-2 w-full h-10 px-1.5 rounded-full transition-colors ${className}`}
      style={{
        background: isLight
          ? "linear-gradient(180deg, rgba(30,20,10,0.06), rgba(30,20,10,0.10))"
          : "linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.03))",
        border: "1px solid rgba(232,207,154,0.28)",
      }}
    >
      <span
        className="absolute top-1 h-8 w-[calc(50%-6px)] rounded-full transition-all duration-300"
        style={{
          left: isLight ? "calc(50% + 2px)" : "4px",
          background: "linear-gradient(135deg, #f2dfb5 0%, #d9b878 55%, #a68547 100%)",
          boxShadow: "0 6px 14px -6px rgba(200,169,106,0.6), inset 0 1px 0 rgba(255,255,255,0.5)",
        }}
      />
      <span
        className={`relative flex-1 text-center text-xs font-medium tracking-wide flex items-center justify-center gap-1.5 ${!isLight ? "text-[#0a0806]" : "text-foreground/70"}`}
      >
        <Moon className="w-3.5 h-3.5" /> Night
      </span>
      <span
        className={`relative flex-1 text-center text-xs font-medium tracking-wide flex items-center justify-center gap-1.5 ${isLight ? "text-[#0a0806]" : "text-foreground/70"}`}
      >
        <Sun className="w-3.5 h-3.5" /> Day
      </span>
    </button>
  );
}
